import { useState, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineDate,
  InlineSelect,
  InlineStatus,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Subsidiary = {
  id: number
  organization: number
  code: string
  name: string
  legal_form: string
  registration_number: string
  tax_id: string
  country_code: string
  address_line1: string
  city: string
  postal_code: string
  ownership_percent: string | null
  start_date: string | null
  end_date: string | null
  consolidation_method: '' | 'FULL' | 'EQUITY' | 'PROPORTIONAL'
  currency_code: string
  accounting_standard: string
  is_active: boolean
  notes: string
}

// ─────────────────── Constants ───────────────────
const ACTIVE_COLOR: Record<'ACTIVE' | 'INACTIVE', string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  INACTIVE: 'bg-gray-100 text-gray-600',
}

const ACCOUNTING_STANDARDS = [
  { value: 'IAS',     label: 'IAS / IFRS',  description: 'International Accounting Standards / IFRS' },
  { value: 'RAS',     label: 'RAS',         description: 'Romanian Accounting Standards (OMFP 1802/2014)' },
  { value: 'HGB',     label: 'HGB',         description: 'German Commercial Code (Handelsgesetzbuch)' },
  { value: 'PCG',     label: 'PCG',         description: 'French Plan Comptable Général' },
  { value: 'US_GAAP', label: 'US GAAP',     description: 'Generally Accepted Accounting Principles (US)' },
  { value: 'UK_GAAP', label: 'UK GAAP',     description: 'FRS 102 (United Kingdom)' },
]

const STANDARD_BADGE: Record<string, string> = {
  IAS:     'bg-blue-100 text-blue-800',
  RAS:     'bg-amber-100 text-amber-800',
  HGB:     'bg-slate-100 text-slate-800',
  PCG:     'bg-purple-100 text-purple-800',
  US_GAAP: 'bg-green-100 text-green-800',
  UK_GAAP: 'bg-teal-100 text-teal-800',
}

const CONSOLIDATION_LABEL: Record<NonNullable<Subsidiary['consolidation_method']>, string> = {
  '': '—',
  FULL: 'Full consolidation',
  EQUITY: 'Equity method',
  PROPORTIONAL: 'Proportional',
}

type TabKey = 'overview' | 'files'

// ─────────────────── Page ───────────────────
export default function SubsidiaryDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const subId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const subQ = useQuery({
    queryKey: ['subsidiary', subId],
    queryFn: async () => (await api.get<Subsidiary>(`/subsidiaries/${subId}/`)).data,
    enabled: !!subId,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Subsidiary>) =>
      (await api.patch(`/subsidiaries/${subId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subsidiary', subId] })
      qc.invalidateQueries({ queryKey: ['subsidiaries'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/subsidiaries/${subId}/`),
    onSuccess: () => {
      toast.success('Subsidiary deleted')
      qc.invalidateQueries({ queryKey: ['subsidiaries'] })
      navigate('/admin/organization')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('overview')

  if (subQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (subQ.isError || !subQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Subsidiary not found.</p>
          <Link to="/admin/organization" className="text-blue-600 hover:underline">← Back to organization</Link>
        </div>
      </AdminLayout>
    )
  }

  const sub = subQ.data

  const save = (payload: Partial<Subsidiary>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/organization" className="text-muted-foreground hover:text-foreground hover:underline">← Organization</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{sub.code}</div>
            <h2 className="text-2xl font-bold text-foreground">
              <InlineText value={sub.name} onSave={(v) => save({ name: v })} />
            </h2>
            <div className="text-sm text-muted-foreground">
              {sub.legal_form && <span>{sub.legal_form}</span>}
              {sub.legal_form && sub.country_code && <span> · </span>}
              {sub.country_code && <span className="font-mono">{sub.country_code}</span>}
              {sub.ownership_percent && (
                <>
                  {' · '}
                  <span className="font-mono">{Number(sub.ownership_percent).toFixed(2)}% owned</span>
                </>
              )}
              {sub.start_date && (
                <>
                  {' · '}
                  <span>{sub.start_date}</span>
                  {sub.end_date && <span> → {sub.end_date}</span>}
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <InlineStatus
              value={sub.is_active ? 'ACTIVE' : 'INACTIVE'}
              colorMap={ACTIVE_COLOR}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
              onSave={(v) => save({ is_active: v === 'ACTIVE' })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => confirm(
                    `subsidiary ${sub.code} (${sub.name})`,
                    () => deleteMut.mutate(),
                  )}
                  className="text-destructive"
                >
                  Delete subsidiary
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['overview', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'overview' && 'Overview'}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'overview' && (
      <div className="space-y-6 max-w-3xl">
        <Section title="Identity & legal">
          <Row label="Code">
            <InlineText
              value={sub.code}
              onSave={(v) => save({ code: v })}
              className="font-mono"
            />
          </Row>
          <Row label="Legal name">
            <InlineText value={sub.name} onSave={(v) => save({ name: v })} />
          </Row>
          <Row label="Legal form">
            <InlineText
              value={sub.legal_form}
              onSave={(v) => save({ legal_form: v })}
              placeholder="GmbH, LLC, SARL, SA, Ltd, …"
            />
          </Row>
          <Row label="Registration number">
            <InlineText
              value={sub.registration_number}
              onSave={(v) => save({ registration_number: v })}
              className="font-mono"
              helper="Commercial register / Companies House number."
            />
          </Row>
          <Row label="Tax ID">
            <InlineText
              value={sub.tax_id}
              onSave={(v) => save({ tax_id: v })}
              className="font-mono"
            />
          </Row>
        </Section>

        <Section title="Location">
          <Row label="Country (ISO 2)">
            <InlineText
              value={sub.country_code}
              onSave={(v) => save({ country_code: v.toUpperCase().slice(0, 2) })}
              className="font-mono"
            />
          </Row>
          <Row label="Address">
            <InlineText
              value={sub.address_line1}
              onSave={(v) => save({ address_line1: v })}
            />
          </Row>
          <Row label="City">
            <InlineText value={sub.city} onSave={(v) => save({ city: v })} />
          </Row>
          <Row label="Postal code">
            <InlineText
              value={sub.postal_code}
              onSave={(v) => save({ postal_code: v })}
            />
          </Row>
        </Section>

        <Section title="Ownership & consolidation">
          <Row label="Ownership %">
            <InlineNumber
              value={sub.ownership_percent || ''}
              step="0.01"
              onSave={(v) => save({ ownership_percent: v === '' ? null : v } as Partial<Subsidiary>)}
              displayFormat={(v) => v
                ? <span className="font-mono">{Number(v).toFixed(2)}%</span>
                : <Dash />}
              helper="0–100. Percentage of equity owned by the holding."
            />
          </Row>
          <Row label="Start date">
            <InlineDate
              value={sub.start_date}
              onSave={(v) => save({ start_date: v })}
            />
          </Row>
          <Row label="End date">
            <InlineDate
              value={sub.end_date}
              onSave={(v) => save({ end_date: v })}
            />
          </Row>
          <Row label="Consolidation method">
            <InlineSelect
              value={sub.consolidation_method || ''}
              displayLabel={sub.consolidation_method
                ? CONSOLIDATION_LABEL[sub.consolidation_method]
                : <span className="italic text-muted-foreground">inherit org default</span>}
              allowClear
              options={[
                { value: 'FULL', label: 'Full consolidation' },
                { value: 'EQUITY', label: 'Equity method' },
                { value: 'PROPORTIONAL', label: 'Proportional' },
              ]}
              onSave={(v) => save({ consolidation_method: v as Subsidiary['consolidation_method'] })}
              helper="Override the org default. Clear to inherit."
            />
          </Row>
        </Section>

        <Section title="Reporting">
          <Row label="Currency">
            <InlineText
              value={sub.currency_code}
              onSave={(v) => save({ currency_code: v.toUpperCase().slice(0, 3) })}
              className="font-mono"
              placeholder="inherit org reporting currency"
              helper="3-letter ISO code. Blank to inherit."
            />
          </Row>
          <Row label="Accounting standard">
            <InlineSelect
              value={sub.accounting_standard || ''}
              displayLabel={sub.accounting_standard ? (() => {
                const s = ACCOUNTING_STANDARDS.find(x => x.value === sub.accounting_standard)
                return (
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STANDARD_BADGE[sub.accounting_standard] ?? 'bg-muted text-muted-foreground'}`}>
                    {s?.label ?? sub.accounting_standard}
                  </span>
                )
              })() : <span className="italic text-muted-foreground">inherit org standard</span>}
              allowClear
              options={ACCOUNTING_STANDARDS.map(s => ({ value: s.value, label: `${s.label} — ${s.description}` }))}
              onSave={(v) => save({ accounting_standard: v })}
              helper="Override the organisation default. Clear to inherit."
            />
          </Row>
        </Section>

        <Section title="Notes">
          <Row label="Notes">
            <InlineText
              value={sub.notes}
              multiline
              onSave={(v) => save({ notes: v })}
              placeholder="Internal notes about this subsidiary"
            />
          </Row>
        </Section>
      </div>
      )}
      {tab === 'files' && <FilesTab parent="core.subsidiary" parentId={subId} />}
    </AdminLayout>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}
